import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import logger from '../utils/logger';

/**
 * ErrorBoundary — catches unhandled JS errors in the component tree
 * and renders a fallback UI instead of crashing the entire app.
 *
 * Usage:
 *   <ErrorBoundary module="Training">
 *     <CareerModule />
 *   </ErrorBoundary>
 *
 * Props:
 *   module      — (string) Name of the module, shown in the fallback UI
 *   onReset     — (function, optional) Called when user clicks "Try Again"
 *   fallback    — (ReactNode, optional) Custom fallback UI
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Log for debugging — replace with remote logging (e.g., Sentry) in production
    logger.error(`ErrorBoundary${this.props.module ? ` — ${this.props.module}` : ''}`, 'Caught error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/trackai/';
  };

  render() {
    if (this.state.hasError) {
      // If a custom fallback was provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const moduleName = this.props.module || 'This section';
      const isDev = process.env.NODE_ENV === 'development';

      return (
        <div className="flex flex-col items-center justify-center h-full w-full bg-gray-900 text-white p-6">
          <div className="max-w-md w-full text-center space-y-6">
            {/* Icon */}
            <div className="flex justify-center">
              <div className="bg-red-500/20 p-4 rounded-full">
                <AlertTriangle className="w-10 h-10 text-red-400" />
              </div>
            </div>

            {/* Title & Description */}
            <div className="space-y-2">
              <h2 className="text-xl font-semibold text-white">
                Something went wrong
              </h2>
              <p className="text-gray-400 text-sm">
                {moduleName} ran into an unexpected error. Your data is safe — try reloading.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 pt-2">
              <button
                onClick={this.handleReset}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-gray-700 hover:bg-gray-600 text-gray-200 font-medium rounded-lg transition-colors"
              >
                <Home className="w-4 h-4" />
                Go Home
              </button>
            </div>

            {/* Dev-mode error details */}
            {isDev && this.state.error && (
              <details className="text-left mt-4">
                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-400">
                  Error Details (dev only)
                </summary>
                <pre className="mt-2 p-3 bg-gray-800 rounded-lg text-xs text-red-300 overflow-auto max-h-40 whitespace-pre-wrap">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
